import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Command } from "cmdk";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  Home,
  Shield,
  FileText,
  Settings,
  LogOut,
  User,
  Phone,
  Users,
  Clock,
  Sparkles,
  ExternalLink,
  Flag,
} from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { trpc } from "../lib/trpc";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { data: projects } = trpc.projects.list.useQuery(undefined, {
    enabled: open,
  });

  const handleSelect = useCallback(
    (callback: () => void) => {
      callback();
      onOpenChange(false);
      setSearch("");
    },
    [onOpenChange]
  );

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };

    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50"
            onClick={() => onOpenChange(false)}
          />

          {/* Command Palette */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: -20 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="fixed top-[20%] left-1/2 -translate-x-1/2 w-full max-w-2xl z-50"
          >
            <Command
              className="glass-card overflow-hidden shadow-elevation-xl"
              label="Command Menu"
            >
              {/* Search Input */}
              <div className="flex items-center border-b border-border px-4 py-3">
                <Search className="w-5 h-5 text-muted-foreground mr-3 flex-shrink-0" />
                <Command.Input
                  value={search}
                  onValueChange={setSearch}
                  placeholder="Search for commands, projects, or navigate..."
                  className="flex-1 bg-transparent border-none outline-none text-foreground placeholder:text-muted-foreground"
                />
                <kbd className="hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-2 py-1 text-xs text-muted-foreground">
                  <span className="text-xs">ESC</span>
                </kbd>
              </div>

              <Command.List className="max-h-[400px] overflow-y-auto p-2">
                <Command.Empty className="py-12 text-center text-sm text-muted-foreground">
                  No results found.
                </Command.Empty>

                {/* Quick Actions */}
                {(user?.role === "ADMIN" || user?.role === "PROVISIONER") && (
                  <Command.Group
                    heading="Quick Actions"
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:uppercase mb-2"
                  >
                    <Command.Item
                      onSelect={() => handleSelect(() => navigate("/"))}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-brand-primary/10 hover:text-brand-primary aria-selected:bg-brand-primary/10 aria-selected:text-brand-primary mb-1"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Create New Project</span>
                      <kbd className="ml-auto hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-xs">
                        ⌘N
                      </kbd>
                    </Command.Item>
                  </Command.Group>
                )}

                {/* Navigation */}
                <Command.Group
                  heading="Navigation"
                  className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:uppercase mb-2"
                >
                  <Command.Item
                    onSelect={() => handleSelect(() => navigate("/"))}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-accent aria-selected:bg-accent mb-1"
                  >
                    <Home className="w-4 h-4" />
                    <span>Dashboard</span>
                    <kbd className="ml-auto hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-xs">
                      ⌘H
                    </kbd>
                  </Command.Item>

                  {(user?.role === "ADMIN" ||
                    user?.role === "PROVISIONER" ||
                    user?.role === "REVIEWER") && (
                    <Command.Item
                      onSelect={() => handleSelect(() => navigate("/workflow"))}
                      className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-accent aria-selected:bg-accent mb-1"
                    >
                      <FileText className="w-4 h-4" />
                      <span>Project Queue</span>
                      <kbd className="ml-auto hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-xs">
                        ⌘Q
                      </kbd>
                    </Command.Item>
                  )}

                  {user?.role === "ADMIN" && (
                    <>
                      <Command.Item
                        onSelect={() => handleSelect(() => navigate("/admin"))}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-accent aria-selected:bg-accent mb-1"
                      >
                        <Settings className="w-4 h-4" />
                        <span>Admin Settings</span>
                        <kbd className="ml-auto hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 text-xs">
                          ⌘A
                        </kbd>
                      </Command.Item>
                      <Command.Item
                        onSelect={() => handleSelect(() => navigate("/admin/users"))}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-accent aria-selected:bg-accent mb-1"
                      >
                        <Users className="w-4 h-4" />
                        <span>Manage Users</span>
                      </Command.Item>
                      <Command.Item
                        onSelect={() => handleSelect(() => navigate("/admin/flags"))}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-accent aria-selected:bg-accent mb-1"
                      >
                        <Flag className="w-4 h-4" />
                        <span>Feature Flags</span>
                      </Command.Item>
                      <Command.Item
                        onSelect={() => handleSelect(() => navigate("/admin/audit"))}
                        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-accent aria-selected:bg-accent mb-1"
                      >
                        <Shield className="w-4 h-4" />
                        <span>Audit Log</span>
                      </Command.Item>
                    </>
                  )}
                </Command.Group>

                {/* Recent Projects */}
                {projects && projects.length > 0 && (
                  <Command.Group
                    heading="Recent Projects"
                    className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:uppercase mb-2"
                  >
                    {projects.slice(0, 5).map((project) => {
                      const StatusIcon = {
                        draft: Clock,
                        in_review: FileText,
                        approved: Sparkles,
                        provisioned: Sparkles,
                      }[project.status] || Clock;

                      return (
                        <Command.Item
                          key={project.id}
                          onSelect={() =>
                            handleSelect(() => navigate(`/projects/${project.id}`))
                          }
                          className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-accent aria-selected:bg-accent mb-1"
                        >
                          <StatusIcon className="w-4 h-4 text-brand-accent" />
                          <div className="flex-1 flex items-center justify-between">
                            <span className="truncate">{project.name}</span>
                            <span className="text-xs text-muted-foreground capitalize ml-2">
                              {project.status.replace("_", " ")}
                            </span>
                          </div>
                          <ExternalLink className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100" />
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                )}

                {/* Account */}
                <Command.Group
                  heading="Account"
                  className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:uppercase"
                >
                  <Command.Item
                    onSelect={() => handleSelect(() => logout())}
                    className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm cursor-pointer transition-colors hover:bg-brand-danger/10 hover:text-brand-danger aria-selected:bg-brand-danger/10 aria-selected:text-brand-danger"
                  >
                    <LogOut className="w-4 h-4" />
                    <span>Sign Out</span>
                    <span className="ml-auto text-xs text-muted-foreground">
                      {user?.email}
                    </span>
                  </Command.Item>
                </Command.Group>
              </Command.List>

              {/* Footer */}
              <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground flex items-center justify-between">
                <span>Use ↑↓ to navigate, ↵ to select, ESC to close</span>
                <div className="flex items-center gap-2">
                  <kbd className="rounded border border-border bg-muted px-1.5 py-0.5">
                    ⌘K
                  </kbd>
                  <span>to toggle</span>
                </div>
              </div>
            </Command>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
